import React, { type RefObject, useLayoutEffect, useRef, useState } from 'react'

import { IMediaItem } from 'types/media'
import { getMediaItemFrameAabbLogical } from 'utils/canvasSelectionAabb'

import MediaControls from './index'
import * as Styled from './styled'
import { POSITIONED_MEDIA_CONTROLS_HOST_CLASS_NAME } from '../consts'

const TOOLBAR_GAP_LOGICAL = 4

/**
 * Keep the toolbar inside the viewport on a single axis.
 *
 * When the toolbar fits (`size <= extent`) it is clamped so it never spills past
 * either edge. When it is larger than the viewport on that axis — which happens
 * on mobile, where the canvas is scaled down so much that the constant-size
 * toolbar becomes wider than the whole canvas — it can't be fully contained, so
 * we center it. That keeps the overflow symmetric (the toolbar's middle stays in
 * view) instead of pinning it to the left edge and letting it run off the right.
 */
const clampAxis = (pos: number, size: number, extent: number) => {
  if (size > extent) {
    return (extent - size) / 2
  }
  return Math.min(Math.max(pos, 0), extent - size)
}

const clampToolbar = (
  left: number,
  top: number,
  tw: number,
  th: number,
  canvasWidth: number,
  canvasHeight: number
) => ({
  left: clampAxis(left, tw, canvasWidth),
  top: clampAxis(top, th, canvasHeight)
})

interface IProps {
  isVisible: boolean
  item: IMediaItem
  displayWidth: number
  displayHeight: number
  itemId: string
  /**
   * Base stacking value from the layer (max clip z-index + 1). The host adds +1
   * so the toolbar sits above that baseline inside the overlay.
   */
  hostZIndex: number
  targetRefsMap: React.RefObject<Map<string, HTMLDivElement>>
  overlayRef: RefObject<HTMLDivElement | null>
  canvasScale: number
  canvasWidth: number
  canvasHeight: number
  onOpenZOrderMenuFromToolbar?: (position: { x: number; y: number }) => void
}

export const PositionedMediaControls: React.FC<IProps> = ({
  isVisible,
  item,
  displayWidth,
  displayHeight,
  itemId,
  hostZIndex,
  targetRefsMap,
  overlayRef,
  canvasScale,
  canvasWidth,
  canvasHeight,
  onOpenZOrderMenuFromToolbar
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [hostPos, setHostPos] = useState({ left: 0, top: 0 })
  const inverseScale = canvasScale > 0 ? 1 / canvasScale : 1

  useLayoutEffect(() => {
    if (!isVisible) {
      return
    }

    let raf = 0
    const s = canvasScale > 0 ? canvasScale : 1

    const tick = () => {
      const overlay = overlayRef.current
      if (!overlay) {
        raf = requestAnimationFrame(tick)
        return
      }

      const frameEl = targetRefsMap.current.get(itemId)
      const toolbarEl = toolbarRef.current

      const o = overlay.getBoundingClientRect()

      let logicalFrameLeft: number
      let logicalFrameTop: number

      if (frameEl) {
        const f = frameEl.getBoundingClientRect()
        logicalFrameLeft = (f.left - o.left) / s
        logicalFrameTop = (f.top - o.top) / s
      } else {
        const aabb = getMediaItemFrameAabbLogical(
          item,
          displayWidth,
          displayHeight
        )
        logicalFrameLeft = aabb.minX
        logicalFrameTop = aabb.minY
      }

      let tw = 120
      let th = 36
      if (toolbarEl) {
        const t = toolbarEl.getBoundingClientRect()
        tw = t.width / s
        th = t.height / s
      }

      const naturalToolbarLeft = logicalFrameLeft
      const naturalToolbarTop = logicalFrameTop - th - TOOLBAR_GAP_LOGICAL

      const { left: cLeft, top: cTop } = clampToolbar(
        naturalToolbarLeft,
        naturalToolbarTop,
        tw,
        th,
        canvasWidth,
        canvasHeight
      )

      const hostLeft = cLeft
      const hostTop = cTop + th + TOOLBAR_GAP_LOGICAL

      setHostPos(prev => {
        if (
          Math.abs(prev.left - hostLeft) < 0.25 &&
          Math.abs(prev.top - hostTop) < 0.25
        ) {
          return prev
        }
        return { left: hostLeft, top: hostTop }
      })

      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
    }
  }, [
    isVisible,
    overlayRef,
    itemId,
    targetRefsMap,
    item,
    displayWidth,
    displayHeight,
    canvasScale,
    canvasWidth,
    canvasHeight
  ])

  if (!isVisible) {
    return null
  }

  const hostStackingZIndex = hostZIndex + 1

  return (
    <Styled.PositionedMediaControlsHost
      className={POSITIONED_MEDIA_CONTROLS_HOST_CLASS_NAME}
      style={{
        left: hostPos.left,
        top: hostPos.top,
        zIndex: hostStackingZIndex
      }}
    >
      <Styled.CounterScaleWrapper $inverseScale={inverseScale}>
        <MediaControls
          ref={toolbarRef}
          onOpenZOrderMenuFromToolbar={onOpenZOrderMenuFromToolbar}
        />
      </Styled.CounterScaleWrapper>
    </Styled.PositionedMediaControlsHost>
  )
}
